import React, { useEffect, useMemo, useRef, useState } from "react";
import DynamicForm from "../components/DynamicForm.jsx";
import GenerateDoc from "../components/GenerateDoc.jsx";
import * as XLSX from "xlsx";
import { getDownloadURL, ref as sRef, getBytes } from "firebase/storage";
import { storage } from "../firebase/firebase";
import RelationFilter from "../components/RelationFilter";
import MultiRelationFilter from "../components/MultiRelationFilter";
import {
  addDossierRecord,
  updateDossierRecord,
  uploadToStorage,
  addDocumentMeta,
  extractConfigSnapshotFromModele,
} from "../services/data";
import { db } from "../firebase/firebase";
import { doc, getDoc, updateDoc, deleteDoc, serverTimestamp, collection, query, where, orderBy, limit, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/firebase";

//import { loadLocal, saveLocal } from "../services/storageBridge";

async function loadABFromStoragePath(path) {
  // 1) Try getBytes first (faster when App Check passes)
  try {
    const bytes = await getBytes(sRef(storage, path));
    return bytes.buffer;
  } catch (_) {
    // 2) Official fallback: getDownloadURL -> fetch without hardcoded domain
    const url = await getDownloadURL(sRef(storage, path));
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
    return await res.arrayBuffer();
  }
}

// --- Detection / parsing helpers ---
const readAsArrayBuffer = async (url) => {
  // supporte blob:, data:, http(s)
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
  return await res.arrayBuffer();
};
const sniffFormat = (ab) => {
  const u8 = new Uint8Array(ab.slice(0, 8));
  // ZIP -> XLSX
  if (u8[0] === 0x50 && u8[1] === 0x4b) return "xlsx";
  // JSON simple
  let head = "";
  try {
    head = new TextDecoder("utf-8").decode(u8).trim();
  } catch {}
  if (head.startsWith("{") || head.startsWith("[")) return "json";
  return "unknown";
};
const extFromName = (name = "") => {
  const m = String(name)
    .toLowerCase()
    .match(/\.(json|xlsx|xls)$/i);
  return m ? m[1] : "";
};

const sanitizeDocFileName = (name = "document.docx") => {
  const trimmed = String(name || "").trim() || "document.docx";
  const cleaned = trimmed.replace(/[^\w.\-]+/g, "_").slice(0, 180);
  return cleaned.toLowerCase().endsWith(".docx") ? cleaned : `${cleaned}.docx`;
};

const buildDossierFolder = (dossierKey = "") => {
  const safeKey = String(dossierKey || "Propositions").replace(
    /[^\w.\-]+/g,
    "_"
  );
  return `dossiers/${safeKey}`;
};

const resolveDossierKey = ({ modele, record, values, prefill }) => {
  const candidates = [
    record?.dossierKey,
    record?.dossier,
    modele?.dossier,
    modele?.dossierKey,
    prefill?.dossierKey,
    values?.dossierKey,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "Propositions";
};

const normalizeInlineConfig = (payload = {}) => {
  try {
    const sectionsRaw = Array.isArray(payload.sections) ? payload.sections : [];
    const fieldsRaw = Array.isArray(payload.fields) ? payload.fields : [];
    const mapsRaw =
      payload.maps && typeof payload.maps === "object" ? payload.maps : {};

    const sectionsList =
      sectionsRaw.length > 0
        ? sectionsRaw
        : [{ id: "general", label: "General", order: 1 }];

    const fieldsDict = {};
    fieldsRaw.forEach((field, idx) => {
      const key = String(field?.key || "").trim();
      if (!key) return;

      const sectionId =
        field.sectionId || field.section || sectionsList[0]?.id || "general";
      const advanced = field.advanced || {};

      const rawOptions = field.options;
      const options = Array.isArray(rawOptions)
        ? rawOptions.map((opt) => String(opt).trim()).filter(Boolean)
        : typeof rawOptions === "string"
        ? rawOptions
            .split(",")
            .map((opt) => opt.trim())
            .filter(Boolean)
        : [];

      const useMapValue =
        advanced.map_key ||
        advanced.use_map ||
        field.map_key ||
        field.use_map ||
        "";

      const orderValue =
        field.order !== undefined
          ? Number(field.order)
          : advanced.order !== undefined
          ? Number(advanced.order)
          : idx;

      fieldsDict[key] = {
        key,
        label: field.label || key,
        type: String(field.type || "text").toLowerCase(),
        required: !!field.required,
        placeholder: field.placeholder || "",
        default: field.default ?? "",
        options,
        order: orderValue,
        visibleIf:
          advanced.visible_if ||
          advanced.visibleIf ||
          field.visible_if ||
          field.visibleIf ||
          "",
        readOnly:
          advanced.read_only !== undefined
            ? !!advanced.read_only
            : !!field.read_only,
        formula: advanced.formula || field.formula || "",
        section: String(sectionId),
        template_if_checked: advanced.template_if_checked || "",
        template_if_unchecked:
          advanced.template_if_unchecked || field.template_if_unchecked || "",
        max_length:
          advanced.max_length !== undefined
            ? Number(advanced.max_length)
            : field.max_length
            ? Number(field.max_length)
            : null,
        regex_validation:
          advanced.regex_validation || field.regex_validation || "",
        mask: advanced.mask || field.mask || "",
        help_text: advanced.help_text || field.help_text || "",
        tooltip: advanced.tooltip || field.tooltip || "",
        group_key: advanced.group_key || field.group_key || "",
        repeat_section:
          advanced.repeat_section !== undefined
            ? !!advanced.repeat_section
            : !!field.repeat_section,
        multi_select:
          advanced.multi_select !== undefined
            ? !!advanced.multi_select
            : !!field.multi_select,
        use_map: useMapValue,
      };

      if (field.item && typeof field.item === "object") {
        fieldsDict[key].item = field.item;
      }
    });

    const sections = sectionsList
      .map((section, idx) => {
        const id = String(section?.id || "").trim() || `section_${idx + 1}`;
        const label =
          section.label ||
          section.name ||
          section.title ||
          `Section ${idx + 1}`;
        const order = Number(section.order ?? idx + 1);
        const fieldsInSection = Object.values(fieldsDict)
          .filter((field) => field.section === id)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((field) => field.key);

        return {
          id,
          label,
          fields: fieldsInSection,
          order,
        };
      })
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    const sectionIds = new Set(sections.map((section) => section.id));
    Object.values(fieldsDict).forEach((field) => {
      if (!sectionIds.has(field.section)) {
        const defaultSection = sections[0];
        if (defaultSection) {
          defaultSection.fields.push(field.key);
          field.section = defaultSection.id;
        }
      }
    });

    const outputNameTemplate =
      payload.meta?.outputNameTemplate ||
      payload.meta?.output_name_template ||
      "Document-{{timestamp}}.docx";

    return {
      outputNameTemplate,
      sections,
      fields: fieldsDict,
      maps: mapsRaw,
    };
  } catch (err) {
    console.warn("[UseModele] normalizeInlineConfig failed", err);
    return null;
  }
};

async function resolveDownloadURL({ rawUrl, storagePath, uid }) {
  console.log("resolveDownloadURL called with:", { rawUrl, storagePath, uid });

  // 1) URLs locales (blob: ou data:)
  if (rawUrl && (rawUrl.startsWith("blob:") || rawUrl.startsWith("data:"))) {
    return rawUrl;
  }

  // 2) URLs Firebase Storage (gs://)
  if (rawUrl && rawUrl.startsWith("gs://")) {
    try {
      return await getDownloadURL(sRef(storage, rawUrl));
    } catch (error) {
      console.error("Error converting gs:// URL:", error);
      throw error;
    }
  }

  // 3) URLs HTTP/HTTPS directes
  if (
    rawUrl &&
    (rawUrl.startsWith("http://") || rawUrl.startsWith("https://"))
  ) {
    return rawUrl;
  }

  // 4) PrioritA au storagePath (utiliser tel quel)
  if (storagePath) {
    try {
      return await getDownloadURL(sRef(storage, storagePath));
    } catch (error) {
      console.error("Storage path access failed:", error);
      throw new Error(
        "AccAs refusA au fichier de stockage. VArifiez les permissions."
      );
    }
  }

  throw new Error("Aucune URL exploitable (verifiez storagePath/fileURL)");
}

export default function UseModele({
  user,
  modele,
  onBack,
  recordToEdit,
  prefillData,
}) {
  const [templateAB, setTemplateAB] = useState(null);
  const [config, setConfig] = useState(null);
  const [tags, setTags] = useState([]);
  const [values, setValues] = useState(recordToEdit?.values || {});
  const [imageKeys, setImageKeys] = useState([]);
  const [showRelationFilter, setShowRelationFilter] = useState(false);
  const [showPlaceholders, setShowPlaceholders] = useState(false);
  const [showMultiRelationFilter, setShowMultiRelationFilter] = useState(false);
  const [multiRelations, setMultiRelations] = useState([]);
  const [multiIndex, setMultiIndex] = useState(0);
  const [recordId, setRecordId] = useState(recordToEdit?.id || null);
  const [resolvedModele, setResolvedModele] = useState(null);
  const [saving, setSaving] = useState(false);
  const mm = modele || resolvedModele;
  const resolving =
    !!recordToEdit?.modeleId &&
    !!user?.uid &&
    (!mm || (!mm.fileURL && !mm.storagePath));
  const hasSource = !!(mm?.fileURL || mm?.storagePath);

  // ---- Live Preview (DOCX -> HTML) ----
  const [docHtmlBase, setDocHtmlBase] = useState("");
  const [docHtmlErr, setDocHtmlErr] = useState("");
  const [docHtmlLoading, setDocHtmlLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const [mobileTab, setMobileTab] = useState("form"); // "form" | "preview"
  const [focusedKey, setFocusedKey] = useState(null);
  const [changedKey, setChangedKey] = useState(null);

  const previewScrollRef = useRef(null);
  const changedTimerRef = useRef(null);

  // --- Sharing State ---
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareConfig, setShareConfig] = useState({ expiresInDays: 30, maxSubmissions: "" });
  const [generatedLink, setGeneratedLink] = useState(null);
  const [sharingLoading, setSharingLoading] = useState(false);
  const [printingLoading, setPrintingLoading] = useState(false);
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [showSubmissions, setShowSubmissions] = useState(false);
  const [viewingSubmission, setViewingSubmission] = useState(null); // for JSON modal

  const [shareTab, setShareTab] = useState("create");
  const [existingLinks, setExistingLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [shareToast, setShareToast] = useState(null); // { type: "success" | "danger", message: string }
  const shareToastTimerRef = useRef(null);

  const pushShareToast = (message, type = "success") => {
    if (shareToastTimerRef.current) clearTimeout(shareToastTimerRef.current);
    setShareToast({ type, message });
    shareToastTimerRef.current = setTimeout(() => setShareToast(null), 2500);
  };

  const handleCreateShareLink = async () => {
    if (!mm) return;
    setSharingLoading(true);
    try {
      // 1. Get config snapshot
      let snapshot = config; 
      if (!snapshot) {
        // Fallback if not loaded in memory yet
        snapshot = await extractConfigSnapshotFromModele(mm);
      }
      if (!snapshot) {
        pushShareToast("Impossible d'extraire la configuration du modèle. Vérifiez qu'il est valide.", "danger");
        setSharingLoading(false);
        return;
      }

      // 2. Call Cloud Function
      const createLink = httpsCallable(functions, "createShareLink");

      // ✅ on construit un id robuste
      const templateId = mm?.id || mm?.modeleId || recordToEdit?.modeleId || "";
      if (!templateId) {
        pushShareToast("Impossible de générer le lien: templateId introuvable pour ce modèle.", "danger");
        setSharingLoading(false);
        return;
      }

      const payload = {
        templateId,           // ✅ requis par la Function
        modeleId: templateId, // ✅ optionnel (compat)
        title: mm?.nom || "Formulaire sans titre",
        configSnapshot: snapshot,
        expiresInDays: Number(shareConfig.expiresInDays) || 0,
        maxSubmissions: shareConfig.maxSubmissions
          ? Number(shareConfig.maxSubmissions)
          : null
      };
      
      const res = await createLink(payload);
      const { token } = res.data;
      const url = `${window.location.origin}/s/${token}`;
      
      const days = Number(shareConfig.expiresInDays || 0);
      const expiresAtTs =
        days > 0 ? new Date(Date.now() + days * 24 * 60 * 60 * 1000) : null;

      setGeneratedLink({
        token,
        url,
        status: "active",
        createdAt: new Date(),
        expiresAt: expiresAtTs,
        maxSubmissions: shareConfig.maxSubmissions ? Number(shareConfig.maxSubmissions) : null
      });

      setShareTab("links");
      pushShareToast("Lien généré.", "success");
      
      fetchExistingLinks();
      fetchRecentSubmissions();

    } catch (err) {
      console.error("Share link creation failed:", err);
      pushShareToast("Erreur lors de la création du lien : " + (err.message || "Inconnue"), "danger");
    } finally {
      setSharingLoading(false);
    }
  };

  const validateValuesForOutput = (vals) => {
    const missing = [];
    const invalid = [];

    (fields || []).forEach((f) => {
      if (!f?.required) return;

      const label = f.label || f.key;
      const isRepeat = !!f.repeat_section && !!f.group_key;

      if (isRepeat) {
        const arr = Array.isArray(vals?.[f.group_key]) ? vals[f.group_key] : [];
        if (!arr.length) {
          missing.push(`${label} (aucune ligne)`);
          return;
        }
        arr.forEach((row, idx) => {
          const v = row?.[f.key];
          if (v === "" || v == null) missing.push(`${label} (ligne ${idx + 1})`);
        });
      } else {
        const v = vals?.[f.key];
        if (v === "" || v == null) missing.push(label);
      }
    });

    (fields || []).forEach((f) => {
      const pattern = f?.regex_validation;
      if (!pattern) return;

      const label = f.label || f.key;
      const isRepeat = !!f.repeat_section && !!f.group_key;

      const testOne = (v, suffix = "") => {
        if (v === "" || v == null) return;
        try {
          const reObj = new RegExp(pattern);
          if (!reObj.test(String(v))) invalid.push(`${label}${suffix}`);
        } catch (e) {
          console.warn("Invalid regex_validation:", pattern, e);
        }
      };

      if (isRepeat) {
        const arr = Array.isArray(vals?.[f.group_key]) ? vals[f.group_key] : [];
        arr.forEach((row, idx) => testOne(row?.[f.key], ` (ligne ${idx + 1})`));
      } else {
        testOne(vals?.[f.key]);
      }
    });

    return { missing, invalid };
  };

  const handlePrintDocument = async () => {
    if (isPrinting) return;

    if (!docHtmlBase || !printablePreviewHtml) {
      alert("Aperçu non disponible pour impression. Attends que le modèle se charge.");
      return;
    }

    // Même logique que le bouton "Générer" : champs requis non vides
    const missing = (requiredKeys || []).filter((k) => {
      const v = (previewValues || {})[k];
      return v === undefined || v === null || String(v).trim() === "";
    });

    if (missing.length) {
      alert(`Champs requis manquants : ${missing.join(", ")}`);
      return;
    }

    try {
      setIsPrinting(true);

      const iframe = document.createElement("iframe");
      iframe.setAttribute("title", "print-frame");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.style.opacity = "0";
      iframe.style.pointerEvents = "none";
      document.body.appendChild(iframe);

      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) throw new Error("print iframe document not available");

      const title = mm?.nom ? String(mm.nom) : "Impression";

      doc.open();
      doc.write(`<!doctype html><html><head><meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${title}</title>
<style>
  body{margin:0;padding:20px;font-family:Arial,Helvetica,sans-serif;color:#111;background:#fff;}
  .print-root{max-width:900px;margin:0 auto;}
  table{width:100%;border-collapse:collapse;}
  td,th{vertical-align:top;}
  @media print{body{padding:0;} .print-root{max-width:none;}}
</style>
</head><body><div class="print-root">${printablePreviewHtml}</div></body></html>`);
      doc.close();

      // Laisser le temps au navigateur de peindre le DOM avant print()
      setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } finally {
          setTimeout(() => {
            try { document.body.removeChild(iframe); } catch (_) {}
            setIsPrinting(false);
          }, 800);
        }
      }, 60);
    } catch (e) {
      console.error("[print] failed", e);
      alert("Erreur impression : " + (e?.message || String(e)));
      setIsPrinting(false);
    }
  };

  const fetchRecentSubmissions = async () => {
    if (!user?.uid || !mm?.id) return;
    try {
      const q = query(
        collection(db, "users", user.uid, "shared_submissions"),
        where("modeleId", "==", mm.id),
        orderBy("submittedAt", "desc"),
        limit(20)
      );
      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setRecentSubmissions(docs);
    } catch (err) {
      console.warn("Fetch submissions failed:", err);
      // Fallback without orderBy if index missing (though unlikely for simple queries)
      try {
         const q2 = query(
          collection(db, "users", user.uid, "shared_submissions"),
          where("modeleId", "==", mm.id),
          limit(20)
        );
        const snap2 = await getDocs(q2);
        const docs2 = snap2.docs.map(d => ({ id: d.id, ...d.data() }));
        // Client-side sort
        docs2.sort((a,b) => (b.submittedAt?.toMillis?.() || 0) - (a.submittedAt?.toMillis?.() || 0));
        setRecentSubmissions(docs2);
      } catch(e) {
        console.error("Retry fetch failed:", e);
      }
    }
  };

  const toMillisSafe = (ts) => {
    if (!ts) return null;
    if (typeof ts?.toMillis === "function") return ts.toMillis();
    if (typeof ts?.seconds === "number") return ts.seconds * 1000;
    const d = new Date(ts);
    const ms = d.getTime();
    return Number.isFinite(ms) ? ms : null;
  };

  const isLinkExpired = (link) => {
    const expMs = toMillisSafe(link?.expiresAt);
    return expMs != null && expMs < Date.now();
  };

  const fetchExistingLinks = async () => {
    if (!user?.uid || !mm?.id) return;
    setLinksLoading(true);
    try {
      // ✅ pas d'orderBy ici pour éviter les index Firestore
      const q = query(
        collection(db, "public_form_links"),
        where("ownerUid", "==", user.uid),
        limit(200)
      );
      const snap = await getDocs(q);
      let links = snap.docs.map((d) => ({ token: d.id, ...d.data() }));

      // Filtre si le doc contient modeleId/templateId (sinon on garde)
      links = links.filter((l) => {
        const mid = l?.modeleId || l?.templateId;
        return !mid || mid === mm.id;
      });

      // Tri client-side (createdAt desc)
      links.sort((a, b) => (toMillisSafe(b.createdAt) || 0) - (toMillisSafe(a.createdAt) || 0));

      setExistingLinks(links);
    } catch (err) {
      console.error("fetchExistingLinks failed:", err);
      alert("Erreur chargement des liens : " + (err?.message || "inconnue"));
    } finally {
      setLinksLoading(false);
    }
  };

  const buildShareUrl = (token) => `${window.location.origin}/s/${token}`;

  const setLinkStatus = async (token, newStatus) => {
    if (!token) return;
    await updateDoc(doc(db, "public_form_links", token), {
      status: newStatus,
      updatedAt: serverTimestamp(),
    });

    // UI update immédiat
    setExistingLinks((prev) =>
      prev.map((l) => (l.token === token ? { ...l, status: newStatus, updatedAt: Timestamp.now() } : l))
    );
    if (generatedLink?.token === token) {
      setGeneratedLink((prev) => (prev ? { ...prev, status: newStatus } : prev));
    }
  };

  const handleDisableLink = async (token) => {
    const t = token || generatedLink?.token;
    if (!t) return;
    if (!window.confirm("Voulez-vous vraiment désactiver ce lien ? Il ne sera plus utilisable.")) return;

    try {
      await setLinkStatus(t, "disabled");
      await fetchExistingLinks();
      // si c'est le lien affiché en haut (generatedLink), on le masque comme avant
      if (!token) setGeneratedLink(null);
      alert("Lien désactivé.");
    } catch (err) {
      console.error("Error disabling link:", err);
      alert("Erreur lors de la désactivation : " + (err?.message || "inconnue"));
    }
  };

  const handleEnableLink = async (token) => {
    if (!token) return;
    if (!window.confirm("Réactiver ce lien ?")) return;

    try {
      await setLinkStatus(token, "active");
      await fetchExistingLinks();
      alert("Lien réactivé.");
    } catch (err) {
      console.error("Error enabling link:", err);
      alert("Erreur lors de la réactivation : " + (err?.message || "inconnue"));
    }
  };

  const handleDeleteLink = async (token) => {
    if (!token) return;
    if (!window.confirm("Supprimer définitivement ce lien ?")) return;

    try {
      await deleteDoc(doc(db, "public_form_links", token));
      setExistingLinks((prev) => prev.filter((l) => l.token !== token));
      if (generatedLink?.token === token) setGeneratedLink(null);
      alert("Lien supprimé.");
    } catch (err) {
      console.error("Error deleting link:", err);
      alert("Erreur suppression : " + (err?.message || "inconnue"));
    }
  };

  // Quand la fenêtre \"Partager\" s'ouvre, on charge liens + soumissions
  useEffect(() => {
    if (!showShareModal) return;
    fetchExistingLinks();
    fetchRecentSubmissions();
  }, [showShareModal, user?.uid, mm?.id]);

  const { activeLinks, archivedLinks } = useMemo(() => {
    const list = Array.isArray(existingLinks) ? existingLinks : [];
    const active = [];
    const archived = [];
    for (const l of list) {
      const status = l?.status || "active";
      const expired = isLinkExpired(l);
      if (status === "active" && !expired) active.push(l);
      else archived.push(l);
    }
    return { activeLinks: active, archivedLinks: archived };
  }, [existingLinks]);

  const previewValues = useMemo(() => {
    return multiRelations.length > 0 ? (multiRelations[multiIndex] || {}) : values;
  }, [multiRelations, multiIndex, values]);



  const escapeHtml = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const onFieldFocus = (key) => {
    setFocusedKey(key);
  };

  const onFieldChangeKey = (key) => {
    setFocusedKey(key);
    setChangedKey(key);
    if (changedTimerRef.current) window.clearTimeout(changedTimerRef.current);
    changedTimerRef.current = window.setTimeout(() => setChangedKey(null), 900);
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!templateAB) return;

      try {
        setDocHtmlLoading(true);
        setDocHtmlErr("");

        // import dynamique (évite soucis bundler)
        const mammoth = await import("mammoth/mammoth.browser");
        const res = await mammoth.convertToHtml({ arrayBuffer: templateAB });

        if (cancelled) return;

        setDocHtmlBase(res?.value || "");
      } catch (e) {
        console.error("[UseModele] DOCX->HTML preview failed:", e);
        if (!cancelled) setDocHtmlErr("Aperçu indisponible (DOCX→HTML).");
      } finally {
        if (!cancelled) setDocHtmlLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [templateAB]);



  useEffect(() => {
    console.log("[UseModele] mm =", mm);
  }, [mm]);

  useEffect(() => {
    // Reload the modele when editing a record without storage metadata
    const needFetch =
      !!recordToEdit?.modeleId &&
      !!user?.uid &&
      (!mm || (!mm.fileURL && !mm.storagePath));

    if (!needFetch) return;

    (async () => {
      try {
        console.log(
          "[UseModele] resolving modele by id:",
          recordToEdit.modeleId
        );
        const ref = doc(
          db,
          "users",
          user.uid,
          "modeles",
          recordToEdit.modeleId
        );
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setResolvedModele({ id: snap.id, ...snap.data() });
        } else {
          alert("Modele introuvable (supprime ou autre compte).");
        }
      } catch (e) {
        console.error("Load modele by ID failed:", e);
        alert("Erreur de chargement du modele.");
      }
    })();
  }, [mm?.fileURL, mm?.storagePath, recordToEdit?.modeleId, user?.uid]);

  // Remove image fields (binary payloads) before saving
  function pruneValuesForStorage(allValues, imgKeys = []) {
    const out = {};
    for (const [k, v] of Object.entries(allValues || {})) {
      if (imgKeys.includes(k)) continue;
      if (
        v &&
        typeof v === "object" &&
        ("bytes" in v || v?.bytes instanceof Uint8Array)
      )
        continue;
      out[k] = v;
    }
    return out;
  }

  function computePeriod(values = {}) {
    const firstDate =
      values?.date ||
      values?.echeance ||
      values?.deadline ||
      values?.date_operation ||
      values?.date_op ||
      values?.dateFacture ||
      values?.invoice_date ||
      new Date().toISOString().slice(0, 10);

    const d = new Date(firstDate);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return {
      periodDay: `${y}-${m}-${day}`,
      periodMonth: `${y}-${m}`,
      periodYear: String(y),
      periodDayLabel: `${day}/${m}/${y}`,
      periodMonthLabel: `${m}/${y}`,
    };
  }

  // 1) charger le .docx
  useEffect(() => {
    (async () => {
      if (!mm) return;

      console.log("Y Starting template loading process...");
      console.log("Modele details:", {
        fileURL: mm.fileURL,
        storagePath: mm.storagePath,
        configURL: mm.configURL,
        configPath: mm.configPath,
        uid: user?.uid,
        nom: mm.nom,
      });

      try {
        let ab = null;
        const hadStorage = !!mm.storagePath;

        if (mm.storagePath) {
          try {
            console.log("Trying storagePath...");
            ab = await loadABFromStoragePath(mm.storagePath);
            console.log("a Storage path successful");
          } catch (e) {
            console.warn("a Storage path failed:", e.message);
          }
        }

        if (!ab) {
          console.log("Trying fileURL fallback...");
          if (mm.fileURL) {
            const url = await resolveDownloadURL({
              rawUrl: mm.fileURL,
              storagePath: mm.storagePath,
              uid: user?.uid,
            });
            const res = await fetch(url, { cache: "no-store", mode: "cors" });
            if (!res.ok)
              throw new Error(`HTTP ${res.status} ${res.statusText}`);
            ab = await res.arrayBuffer();
            console.log("a FileURL successful");
          } else if (hadStorage) {
            throw new Error(
              "Impossible de charger le modele: storagePath a AchouA et fileURL non disponible."
            );
          } else {
            throw new Error("Aucune source valide pour charger le modele.");
          }
        }

        setTemplateAB(ab);

        const { default: PizZip } = await import("pizzip");
        const zip = new PizZip(ab);
        const parts = [
          "word/document.xml",
          "word/header1.xml",
          "word/header2.xml",
          "word/header3.xml",
          "word/footer1.xml",
          "word/footer2.xml",
          "word/footer3.xml",
        ].filter((p) => zip.file(p));
        if (!parts.length) {
          alert("ModAle .docx invalide");
          return;
        }

        const textKeys = new Set();
        const imgKeys = new Set();
        let foundDoubleBraces = false;

        for (const p of parts) {
          const xml = zip.file(p).asText();
          const joined = Array.from(
            xml.matchAll(/<w:t[^>]*>([^<]*)<\/w:t>/g),
            (m) => m[1]
          ).join("");

          if (/\{\{\s*[\w.-]+\s*\}\}/.test(joined)) foundDoubleBraces = true;
          for (const m of joined.matchAll(/\{%%\s*([\w.-]+)\s*\}/g))
            imgKeys.add(m[1]);
          for (const m of joined.matchAll(/\{%{1}\s*([\w.-]+)\s*\}/g))
            imgKeys.add(m[1]);
          for (const m of joined.matchAll(/\{\s*([\w.-]+)\s*\}/g)) {
            const k = m[1];
            if (!k.startsWith("%")) textKeys.add(k);
          }
        }

        setTags([...textKeys, ...imgKeys]);
        setImageKeys([...imgKeys]);

        if (foundDoubleBraces) {
          console.warn(
            "[UseModele] Balises {{key}} dAtectAes a prAfArez {key}."
          );
        }
      } catch (err) {
        console.error("a Template loading error:", err);
        alert("Erreur de chargement du modele : " + err.message);
      }
    })();
  }, [mm?.fileURL, mm?.storagePath, user?.uid]); // Y deps

  // 2) charger/parse la config
  useEffect(() => {
    (async () => {
      try {
        if (!mm?.configURL && !mm?.configPath) {
          setConfig(null);
          return;
        }

        console.log("Y Config details:", {
          configURL: mm.configURL,
          configPath: mm.configPath,
          uid: user?.uid,
        });

        let ab = null;
        const hadCfgStorage = !!mm.configPath;

        if (mm.configPath) {
          try {
            console.log("Trying config storagePath...");
            ab = await loadABFromStoragePath(mm.configPath);
            console.log("a Config storage path successful");
          } catch (e) {
            console.warn("a Config storage path failed:", e.message);
          }
        }

        if (!ab) {
          console.log("Trying configURL fallback...");
          if (mm.configURL) {
            ab = await readAsArrayBuffer(mm.configURL);
            console.log("a ConfigURL successful");
          } else if (hadCfgStorage) {
            throw new Error(
              "Impossible de charger la config: configPath a AchouA et configURL non disponible."
            );
          } else {
            throw new Error(
              "Aucune source valide pour charger la configuration."
            );
          }
        }

        const nameGuess = (mm.configPath || mm.configURL || "").toLowerCase();
        const byExt = extFromName(nameGuess);
        const kind = byExt || sniffFormat(ab);

        if (kind === "json") {
          const text = new TextDecoder("utf-8").decode(ab);
          const json = JSON.parse(text);
          const normalized = normalizeInlineConfig(json);
          setConfig(normalized || null);
          return;
        }

        if (kind === "xlsx" || kind === "xls") {
          const wb = XLSX.read(ab, { type: "array" });
          const take = (sheet) => {
            const ws = wb.Sheets[sheet];
            if (!ws) return [];
            return XLSX.utils.sheet_to_json(ws, { defval: "" });
          };
          const metaRows = take("Meta");
          let sectionsRows = take("Sections");
          const fieldsRows = take("Fields");
          const arraysRows = take("Arrays");
          const mapsRows = take("Maps")?.length
            ? take("Maps")
            : take("maps") || [];
          let outputNameTemplate = "Document-{{timestamp}}.docx";
          const omt = metaRows.find(
            (r) => String(r.key || "").toLowerCase() === "outputnametemplate"
          );
          if (omt && omt.value) outputNameTemplate = String(omt.value);
          // ---- Regrouper les maps par map_key
          const mapsByKey = {};
          for (const r of mapsRows) {
            const mk = String(r.map_key || "").trim();
            const lbl = String(r.map_label || "").trim();
            const val = r.map_value ?? "";
            const ord = Number(r.order ?? 0);
            const isDef =
              String(r.is_default || "").toLowerCase() === "true" ||
              r.is_default === true;
            if (!mk || !lbl) continue;
            (mapsByKey[mk] ||= []).push({
              map_label: lbl,
              map_value: String(val),
              order: ord,
              is_default: isDef,
            });
          }
          Object.keys(mapsByKey).forEach((k) => {
            mapsByKey[k].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          });
          const fieldsDict = {};
          for (const r of fieldsRows) {
            const key = String(r.key || "").trim();
            if (!key) continue;
            fieldsDict[key] = {
              key,
              label: r.label || key,
              type: String(r.type || "text").toLowerCase(),
              required: !!r.required,
              placeholder: r.placeholder || "",
              default: r.default ?? "",
              options: r.options
                ? String(r.options)
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                : [],
              order: Number(r.order ?? 0),
              visibleIf: r.visible_if || r.visibleIf || "",
              readOnly: !!(r.readOnly || r.read_only || r.locked || r.ro),
              formula: r.formula || r.calc || "",
              section: String(r.section || "").trim(),
              template_if_checked: r.template_if_checked || "",
              template_if_unchecked: r.template_if_unchecked || "",
              max_length: r.max_length ? Number(r.max_length) : null,
              regex_validation: r.regex_validation || "",
              mask: r.mask || "",
              help_text: r.help_text || "",
              tooltip: r.tooltip || "",
              group_key: r.group_key || "",
              repeat_section:
                String(r.repeat_section).trim().toLowerCase() === "true",
              multi_select: !!r.multi_select,
              use_map: r.use_map || r.useMap || "",
            };
          }

          const grouped = {};
          for (const r of arraysRows) {
            const parent = String(r.parent_key || "").trim();
            const sub = String(r.sub_key || "").trim();
            if (!parent || !sub) continue;
            grouped[parent] ??= { fields: {} };
            grouped[parent].fields[sub] = {
              key: sub,
              label: r.label || sub,
              type: String(r.type || "text").toLowerCase(),
              required: !!r.required,
              placeholder: r.placeholder || "",
              default: r.default ?? "",
              options: r.options
                ? String(r.options)
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                : [],
              order: Number(r.order ?? 0),
              visibleIf: r.visible_if || r.visibleIf || "",
            };
          }
          for (const pk of Object.keys(grouped)) {
            if (!fieldsDict[pk]) {
              fieldsDict[pk] = { key: pk, label: pk, type: "array", order: 0 };
            }
            fieldsDict[pk].item = { fields: grouped[pk].fields };
          }

          if (!sectionsRows || !sectionsRows.length) {
            const ids = [
              ...new Set(
                fieldsRows
                  .map((fr) => String(fr.section || "").trim())
                  .filter(Boolean)
              ),
            ];
            sectionsRows = ids.map((id, i) => ({
              id,
              label: id,
              order: i + 1,
            }));
          }
          const secs = sectionsRows
            .map((r) => ({
              id: String(r.id || "").trim(),
              label: r.label || r.id,
              order: Number(r.order ?? 0),
            }))
            .filter((s) => s.id)
            .sort((a, b) => a.order - b.order);

          const sections = secs.map((s) => {
            const keys = Object.keys(fieldsDict)
              .filter((k) => (fieldsDict[k].section || "") === s.id)
              .sort(
                (a, b) =>
                  (fieldsDict[a]?.order ?? 0) - (fieldsDict[b]?.order ?? 0)
              );
            return { id: s.id, label: s.label, fields: keys };
          });

          setConfig({
            outputNameTemplate,
            sections,
            fields: fieldsDict,
            maps: mapsByKey,
          });
          return;
        }

        setConfig(null);
        alert("Format de configuration non supportA (utilise .json ou .xlsx)");
      } catch (err) {
        console.error("a Config loading error:", err);
        setConfig(null);
        alert("Erreur de chargement de la configuration : " + err.message);
      }
    })();
  }, [mm?.configURL, mm?.configPath, user?.uid]);

  const fields = useMemo(() => {
    if (!config?.fields) return [];

    return Object.values(config.fields)
      .map((cfg) => {
        const inDocx = tags.includes(cfg.key);
        // ---- RAsolution maps a options(labels) + mapping labelavalue + default Eventuel
        let options = cfg.options || [];
        let optionsMap = null;
        let mapDefaultValue = undefined;
        if (cfg.use_map && config.maps?.[cfg.use_map]) {
          options = [];
          optionsMap = {};
          for (const row of config.maps[cfg.use_map]) {
            options.push(row.map_label);
            optionsMap[row.map_label] = row.map_value;
            if (row.is_default && mapDefaultValue === undefined)
              mapDefaultValue = row.map_value;
          }
        }
        const resolvedDefault = cfg.default ?? mapDefaultValue ?? "";
        return {
          key: cfg.key,
          label: cfg.label || cfg.key,
          type: cfg.type || "text",
          required: !!cfg.required,
          options,
          optionsMap, // a nouveau
          use_map: cfg.use_map, // a nouveau
          mapDefaultValue, // a nouveau
          placeholder: cfg.placeholder || "",
          default: resolvedDefault,
          order: Number(cfg.order ?? 0),
          visibleIf: cfg.visibleIf || cfg.visible_if || "",
          readOnly: !!(cfg.readOnly || cfg.read_only || cfg.locked || cfg.ro),
          formula: cfg.formula || cfg.calc || "",
          template_if_checked: cfg.template_if_checked || "",
          template_if_unchecked: cfg.template_if_unchecked || "",
          max_length: cfg.max_length || null,
          regex_validation: cfg.regex_validation || "",
          mask: cfg.mask || "",
          group_key: cfg.group_key || "",
          help_text: cfg.help_text || "",
          multi_select: cfg.multi_select || false,
          tooltip: cfg.tooltip || "",
          repeat_section: cfg.repeat_section || "",
          dynamic_options: cfg.dynamic_options || "",
          inDocx, // a NOUVELLE PROPRIATA
        };
      })
      .sort((a, b) => a.order - b.order);
  }, [tags, config]);

  const sections = useMemo(() => {
    return config?.sections || [];
  }, [config]);

  const requiredKeys = useMemo(
    () => fields.filter((f) => f.required).map((f) => f.key),
    [fields]
  );
  const tagKeys = useMemo(() => fields.map((f) => f.key), [fields]);
  const outputNameTemplate = useMemo(
    () => config?.outputNameTemplate || "document_{{timestamp}}.docx",
    [config]
  );
  console.log("FIELDS LOADED:", fields);

  const labelByKey = useMemo(() => {
    const out = {};
    (fields || []).forEach((f) => (out[f.key] = f.label || f.key));
    return out;
  }, [fields]);

  // --- Groupes (Sections répétables) : group_key -> [fields...]
  const repeatGroups = useMemo(() => {
    const groups = {};
    (fields || []).forEach((f) => {
      const g = String(f.group_key || "").trim();
      const isRepeat =
        f.repeat_section === true ||
        String(f.repeat_section || "").toLowerCase() === "true";
      if (!g || !isRepeat) return;
      (groups[g] ||= []).push(f);
    });
    // ordre stable
    Object.values(groups).forEach((arr) =>
      arr.sort((a, b) => (Number(a.order ?? 0) - Number(b.order ?? 0)))
    );
    return groups;
  }, [fields]);

  const renderedPreviewHtml = useMemo(() => {
    if (!docHtmlBase) return "";

    let html = docHtmlBase;

    // --- Normalize docxtemplater tags in Mammoth HTML ---
    // Mammoth can output "{" and "}" as HTML entities or split a tag across multiple spans.
    // Example visually: {#beneficiaire} but HTML may be: <span>{</span><span>#beneficiaire</span><span>}</span>
    html = html
      .replace(/&#123;|&lbrace;|&lcub;/g, "{")
      .replace(/&#125;|&rbrace;|&rcub;/g, "}")
      .replace(/&#160;|&nbsp;/g, " ");

    // Remove HTML tags *inside* any {...} chunk so "{#beneficiaire}" becomes matchable.
    html = html.replace(/\{[\s\S]*?\}/g, (m) => {
      return m
        .replace(/<[^>]*>/g, "")
        .replace(/&#160;|&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    });

    const escapeHtml = (s) =>
      String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const escapeRegExp = (s) =>
      String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const formatValue = (v) => {
      if (Array.isArray(v)) {
        // multiselect -> "a, b" ; section répétable (array d'objets) gérée ailleurs
        if (v.length && typeof v[0] === "object") return "";
        return v.join(", ");
      }
      return String(v ?? "");
    };

    const spanFor = ({ key, label, value, isImg }) => {
      const hasValue = isImg
        ? !!(value && typeof value === "object" && (value.bytes || value.fileName || value.url))
        : !(value === "" || value === null || value === undefined);

      const text = hasValue
        ? (isImg ? `🖼 ${label}` : formatValue(value))
        : `[${label}]`;

      const classes = [
        "um-ph",
        hasValue ? "um-ph--filled" : "um-ph--missing",
        key === focusedKey ? "um-ph--focus" : "",
        key === changedKey ? "um-ph--changed" : "",
        isImg ? "um-ph--img" : "",
      ].filter(Boolean).join(" ");

      return `<span class="${classes}" data-ph-key="${escapeHtml(key)}">${escapeHtml(text)}</span>`;
    };

    const replaceKeyInBlock = (blockHtml, field, value) => {
      const key = field.key;
      const label = (labelByKey?.[key] || field.label || key);
      const isImg = (imageKeys || []).includes(key);
      const span = spanFor({ key, label, value, isImg });

      let out = blockHtml;

      if (isImg) {
        const r1 = new RegExp(`\\{%%\\s*${escapeRegExp(key)}\\s*\\}`, "g");
        const r2 = new RegExp(`\\{%\\s*${escapeRegExp(key)}\\s*\\}`, "g");
        out = out.replace(r1, span).replace(r2, span);
      }

      const rt = new RegExp(`\\{\\s*${escapeRegExp(key)}\\s*\\}`, "g");
      out = out.replace(rt, span);

      return out;
    };

    // 1) Déplier les boucles {#group} ... {/group}
    // Ajoute class/data-* sur le 1er tag du bloc (évite de casser les <table>)
    // Unwrap <p> qui contiennent uniquement {#group} ou {/group}
    // (sinon on casse les <p> quand on remplace la boucle)
    const unwrapSoloLoopParagraphs = (src, key) => {
      const k = escapeRegExp(key);

      const startP = new RegExp(
        `<p\\b[^>]*>\\s*(?:<[^>]+>\\s*)*\\{\\s*#\\s*${k}\\s*\\}\\s*(?:<\\/[^>]+>\\s*)*<\\/p>`,
        "g"
      );
      const endP = new RegExp(
        `<p\\b[^>]*>\\s*(?:<[^>]+>\\s*)*\\{\\s*\\/\\s*${k}\\s*\\}\\s*(?:<\\/[^>]+>\\s*)*<\\/p>`,
        "g"
      );

      return src
        .replace(startP, `{#${key}}`)
        .replace(endP, `{/${key}}`);
    };

    // Ajoute class/data-attrs sur la 1ère balise du bloc (évite de wrap en <div> et casser les tableaux)
    const decorateRepeatRoot = (block, groupKey, idx) => {
      const rootRe = /^(\s*<([a-zA-Z0-9:-]+)\b)([^>]*)(>)/;
      const m = block.match(rootRe);
      if (!m) return null;

      let attrs = m[3] || "";
      const dataAttrs = ` data-group="${escapeHtml(groupKey)}" data-idx="${idx}"`;

      // merge class
      const classRe = /\bclass\s*=\s*"([^"]*)"/i;
      if (classRe.test(attrs)) {
        attrs = attrs.replace(classRe, (_x, cls) => {
          const next = cls ? `${cls} um-repeat-item` : "um-repeat-item";
          return `class="${next}"`;
        });
      } else {
        attrs += ` class="um-repeat-item"`;
      }

      // data attrs
      attrs += dataAttrs;

      const replaced = `${m[1]}${attrs}${m[4]}`;
      return block.replace(rootRe, replaced);
    };

    // --- Cas spécial: boucle placée dans <tr> (table row) -> on répète le <tr> entier
    const expandTableRowLoops = (srcHtml, groupKey, groupFields) => {
      let html = srcHtml;
      const gk = escapeRegExp(groupKey);

      const openRe = new RegExp(`\\{\\s*#\\s*${gk}\\s*\\}`, "g");
      const closeRe = new RegExp(`\\{\\s*\\/\\s*${gk}\\s*\\}`, "g");

      let guard = 0;
      while (guard++ < 50) {
        openRe.lastIndex = 0;
        const open = openRe.exec(html);
        if (!open) break;

        closeRe.lastIndex = open.index + open[0].length;
        const close = closeRe.exec(html);
        if (!close) break;

        // On vérifie si open+close sont dans le MÊME <tr>
        const trStart = html.lastIndexOf("<tr", open.index);
        const trEnd = html.indexOf("</tr>", open.index);
        if (trStart === -1 || trEnd === -1) break;
        if (close.index > trEnd) break; // pas dans le même <tr> => on laisse le traitement "normal"

        const trEndClose = trEnd + 5;
        const rowTpl = html.slice(trStart, trEndClose);

        const arr = Array.isArray(previewValues?.[groupKey]) ? previewValues[groupKey] : [];
        const rows = arr.length ? arr : [{}];

        const openRm = new RegExp(`\\{\\s*#\\s*${gk}\\s*\\}`, "g");
        const closeRm = new RegExp(`\\{\\s*\\/\\s*${gk}\\s*\\}`, "g");

        const rowsHtml = rows
          .map((row, idx) => {
            let block = rowTpl.replace(openRm, "").replace(closeRm, "");

            for (const f of groupFields) {
              block = replaceKeyInBlock(block, f, row?.[f.key]);
            }

            const decorated = decorateRepeatRoot(block, groupKey, idx);
            return decorated || block;
          })
          .join("");

        // Remplacer le <tr> original par n lignes
        html = html.slice(0, trStart) + rowsHtml + html.slice(trEndClose);
      }

      return html;
    };

    // 1) Déplier les boucles {#group} ... {/group}
    const repeatFieldKeys = new Set();
    Object.values(repeatGroups || {}).forEach((arr) => arr.forEach((f) => repeatFieldKeys.add(f.key)));

    for (const [groupKey, groupFields] of Object.entries(repeatGroups || {})) {
      html = unwrapSoloLoopParagraphs(html, groupKey);

      // NEW: si la boucle est dans une ligne de tableau, on répète le <tr> entier
      html = expandTableRowLoops(html, groupKey, groupFields);

      // Ensuite: fallback “normal” (boucles hors-table / paragraphes)
      const loopRe = new RegExp(
        `\\{\\s*#\\s*${escapeRegExp(groupKey)}\\s*\\}([\\s\\S]*?)\\{\\s*\\/\\s*${escapeRegExp(groupKey)}\\s*\\}`,
        "g"
      );

      html = html.replace(loopRe, (_match, inner) => {
        const rows = Array.isArray(previewValues?.[groupKey]) ? previewValues[groupKey] : [];
        const safeRows = rows.length ? rows : [{}];

        const rowsHtml = safeRows
          .map((row, idx) => {
            let block = inner;
            for (const f of groupFields) {
              block = replaceKeyInBlock(block, f, row?.[f.key]);
            }
            return decorateRepeatRoot(block, groupKey, idx);
          })
          .join("");

        return rowsHtml;
      });
    }

    // Nettoyage sécurité (si un tag de boucle reste visible)
    html = html.replace(/\{\s*#\s*[\w.-]+\s*\}|\{\s*\/\s*[\w.-]+\s*\}/g, "");

    // 2) Remplacer les champs "non répétables" (niveau racine)
    for (const f of fields || []) {
      if (!f?.key) continue;
      if (repeatFieldKeys.has(f.key)) continue; // déjà traité dans la boucle
      html = replaceKeyInBlock(html, f, previewValues?.[f.key]);
    }

    return html;
  }, [
    docHtmlBase,
    fields,
    repeatGroups,
    previewValues,
    labelByKey,
    imageKeys,
    focusedKey,
    changedKey,
  ]);

  const printablePreviewHtml = useMemo(() => {
    if (!renderedPreviewHtml) return "";
    // Retire juste l’enrobage visuel des placeholders pour l'impression (garde le texte)
    return renderedPreviewHtml
      .replace(/<span class=\"um-ph[^\"]*\"[^>]*>([\s\S]*?)<\/span>/g, "$1")
      .replace(/\sdata-ph-key=\"[^\"]*\"/g, "");
  }, [renderedPreviewHtml]);

  useEffect(() => {
    if (!focusedKey) return;
    const wrap = previewScrollRef.current;
    if (!wrap) return;

    const el = wrap.querySelector(`[data-ph-key="${focusedKey.replace(/"/g, '\\"')}"]`);
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "center" });

    // petit flash si focus
    el.classList.add("um-ph--flash");
    const t = window.setTimeout(() => el.classList.remove("um-ph--flash"), 700);
    return () => window.clearTimeout(t);
  }, [focusedKey, renderedPreviewHtml]);
  const onSaveRecord = async () => {
    if (!user?.uid) return alert("Utilisateur non connecté");
    if (!mm) return alert("Modèle manquant");

    const safeValues = pruneValuesForStorage(values, imageKeys);
    const valuesWithDossier = {
      ...safeValues,
      dossierKey: effectiveDossierKey,
    };

    const periods = computePeriod(valuesWithDossier);

    const base = (mm.nom || "Doc").replace(/[^\w.\-]+/g, "_");
    const draftName = `${base}-brouillon.docx`;

    const payload = {
      status: "draft",
      statut: "Brouillon",
      dossierKey: effectiveDossierKey,
      dossier: effectiveDossierKey,
      modeleId: mm.id,
      modeleName: mm.nom,
      modeleIcon: mm.icon,
      fileName: draftName,
      values: valuesWithDossier,
      placeholders: tagKeys,
      ...periods,
    };

    if (recordId) {
      await updateDossierRecord(user.uid, recordId, payload);
    } else {
      const { id } = await addDossierRecord(user.uid, payload);
      setRecordId(id);
    }

    alert("Brouillon enregistré dans Firestore");
    onBack && onBack();
  };
  const effectiveDossierKey = useMemo(
    () =>
      resolveDossierKey({
        modele: mm,
        record: recordToEdit,
        values,
        prefill: prefillData,
      }),
    [mm, recordToEdit, values, prefillData]
  );

  const onSaveFinal = async (fileNameFromGen, blob) => {
    setSaving(true);
    if (!user?.uid) return alert("Utilisateur non connecté");
    if (!mm) return alert("Modéle manquant");
    if (!(blob instanceof Blob)) {
      alert(
        "Fichier généré introuvable a impossible d'enregistrer le document."
      );
      return;
    }

    const safeValues = pruneValuesForStorage(values, imageKeys);
    const valuesWithDossier = {
      ...safeValues,
      dossierKey: effectiveDossierKey,
    };
    const periods = computePeriod(valuesWithDossier);
    const normalizedName = sanitizeDocFileName(
      (fileNameFromGen || `Document-${Date.now()}.docx`).replace(
        /[^\w.\-]+/g,
        "_"
      )
    );

    let uploaded = null;
    try {
      const fileToUpload = new File([blob], normalizedName, {
        type:
          blob.type ||
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      uploaded = await uploadToStorage({
        uid: user.uid,
        file: fileToUpload,
        folder: buildDossierFolder(effectiveDossierKey),
        contentType: fileToUpload.type,
      });
    } catch (err) {
      console.error("[UseModele] Upload DOCX failed:", err);
      alert("Erreur lors de l'enregistrement du document gAnArA (upload).");
      return;
    }

    const docTitle =
      (fileNameFromGen || normalizedName || "").trim() || normalizedName;
    const payload = {
      status: "final",
      statut: "Fini",
      dossierKey: effectiveDossierKey,
      dossier: effectiveDossierKey,
      modeleId: mm.id,
      modeleName: mm.nom,
      modeleIcon: mm.icon,
      fileName: normalizedName,
      file: uploaded
        ? {
            name: normalizedName,
            storagePath: uploaded.storagePath,
            url: uploaded.fileURL,
            fileURL: uploaded.fileURL,
            size: uploaded.size,
          }
        : undefined,
      values: valuesWithDossier,
      placeholders: tagKeys,
      ...periods,
    };

    let targetRecordId = recordId;
    if (recordId) {
      await updateDossierRecord(user.uid, recordId, payload);
    } else {
      const { id } = await addDossierRecord(user.uid, payload);
      setRecordId(id);
      targetRecordId = id;
    }

    if (uploaded?.fileURL) {
      await addDocumentMeta(user.uid, {
        title: docTitle,
        fileURL: uploaded.fileURL,
        storagePath: uploaded.storagePath,
        dossierKey: effectiveDossierKey,
        dossierRecordId: targetRecordId || null,
      });
    }
    onBack && onBack();
    alert("Document enregistré avec succès !");
    setSaving(false);
  };

  const shareUrl = generatedLink?.token
    ? `${window.location.origin}/s/${generatedLink.token}`
    : "";

  const activeLinksCount = generatedLink?.status === "active" ? 1 : 0;
  const submissionsCount = recentSubmissions?.length || 0;
  const todayKey = new Date().toDateString();
  const todayCount = (recentSubmissions || []).filter((s) => {
    const ms = s?.submittedAt?.toMillis?.();
    if (!ms) return false;
    return new Date(ms).toDateString() === todayKey;
  }).length;
  
  const submissionsLoading = false; // Placeholder as state is missing

  return (
    <div className="um-root">
      <style>{`
        .um-root{height:100vh; display:flex; flex-direction:column; background:#f6f7fb;}
        .um-header{
          position:sticky; top:0; z-index:50;
          display:flex; align-items:center; justify-content:space-between;
          gap:12px; padding:12px 14px; background:#fff; border-bottom:1px solid rgba(0,0,0,.06);
        }
        .um-head-left{display:flex; align-items:center; gap:10px; min-width:200px;}
        .um-title{font-size:16px; font-weight:800; line-height:1.1;}
        .um-sub{font-size:12px; color:#666;}
        .um-head-actions{display:flex; align-items:center; gap:10px; flex-wrap:wrap; justify-content:flex-end;}
        .um-tabs{display:none; gap:8px; padding:10px 12px; background:#fff; border-bottom:1px solid rgba(0,0,0,.06);}
        .um-tab{flex:1; padding:10px 12px; border-radius:12px; border:1px solid rgba(0,0,0,.08); background:#fff; font-weight:700;}
        .um-tab.active{border-color:rgba(0,0,0,.18); box-shadow:0 1px 10px rgba(0,0,0,.06);}
        .um-body{flex:1; display:grid; grid-template-columns: 1fr 1fr; gap:14px; padding:14px; min-height:0;}
        .um-pane{min-height:0; display:flex; flex-direction:column;}
        .um-card{background:#fff; border-radius:16px; box-shadow:0 2px 16px rgba(0,0,0,.05); border:1px solid rgba(0,0,0,.06); min-height:0; display:flex; flex-direction:column;}
        .um-card-head{padding:12px 12px 0 12px;}
        .um-card-title{display:flex; align-items:center; justify-content:space-between; gap:10px;}
        .um-card-title h2{margin:0; font-size:14px; font-weight:900;}
        .um-toolrow{display:flex; gap:10px; flex-wrap:wrap; margin-top:10px;}
        .um-card-body{padding:12px; overflow:auto; min-height:0;}
        .um-preview-top{padding:12px; display:flex; align-items:center; justify-content:space-between; border-bottom:1px solid rgba(0,0,0,.06);}
        .um-preview-top strong{font-size:14px;}
        .um-preview-scroll{flex:1; overflow:auto; padding:12px; min-height:0;}
        .um-paper{
          background:#fff; border-radius:14px; border:1px solid rgba(0,0,0,.08);
          box-shadow:0 6px 20px rgba(0,0,0,.08);
          max-width:860px; margin:0 auto;
          padding:30px; font-family:"Times New Roman", Times, serif; line-height:1.6;
        }
        .um-paper p{margin:0 0 10px 0;}
        .um-ph{
          display:inline-block; padding:1px 6px; border-radius:10px;
          border:1px dashed rgba(0,0,0,.25);
          background:rgba(255, 214, 102, .35);
          font-family:inherit;
        }
        .um-ph--filled{background:rgba(170, 255, 195, .35); border-style:solid;}
        .um-ph--img{font-family:system-ui, -apple-system, Segoe UI, Roboto, Arial;}
        .um-ph--focus{outline:2px solid rgba(0,0,0,.18); outline-offset:2px;}
        .um-ph--changed{box-shadow:0 0 0 4px rgba(255, 214, 102, .25);}
        .um-ph--flash{animation:umFlash .7s ease;}
        @keyframes umFlash{0%{transform:scale(1)}50%{transform:scale(1.03)}100%{transform:scale(1)}}
        .um-alert{margin:10px 14px;}
        @media (max-width: 992px){
          .um-body{grid-template-columns:1fr;}
          .um-tabs{display:flex;}
          .um-hide-mobile{display:none;}
        }
        .um-repeat-item{margin-bottom:10px;}
        .um-repeat-item:last-child{margin-bottom:0;}

        /* Modal Styles */
        /* Modal Styles moved to App.css */
      `}</style>

      {/* Header sticky */}
      <div className="um-header">
        <div className="um-head-left">
          <button className="btn btn-outline" onClick={onBack}>
            <i className="fas fa-arrow-left"></i> Retour
          </button>
          <div>
            <div className="um-title">{mm?.nom || "Utiliser le modele"}</div>
            <div className="um-sub">Formulaire + aperçu en direct</div>
          </div>
        </div>

        <div className="um-head-actions">
          <button
            className="btn btn-outline"
            onClick={handlePrintDocument}
            disabled={!docHtmlBase || docHtmlLoading || isPrinting}
            title="Imprimer la version actuelle (avec vérification des champs requis)"
          >
            <i className="fas fa-print"></i> Imprimer
          </button>
          
          {mm && hasSource && templateAB && (
            <>
              {multiRelations.length > 0 ? (
                <GenerateDoc
                  templateArrayBuffer={templateAB}
                  values={multiRelations[multiIndex]}
                  tagKeys={tagKeys}
                  requiredKeys={requiredKeys}
                  outputNameTemplate={outputNameTemplate}
                  onAfterGenerate={async (name, blob) => {
                    try {
                      await onSaveFinal(name, blob);
                    } finally {
                      if (multiIndex < multiRelations.length - 1) setMultiIndex((i) => i + 1);
                      else {
                        alert("✅ Batch terminé !");
                        setMultiRelations([]);
                      }
                    }
                  }}
                  auto={true}
                  index={multiIndex}
                  total={multiRelations.length}
                />
              ) : (
                <>
                  <GenerateDoc
                    templateArrayBuffer={templateAB}
                    values={values}
                    tagKeys={tagKeys}
                    requiredKeys={requiredKeys}
                    outputNameTemplate={outputNameTemplate}
                    onAfterGenerate={async (name, blob) => {
                      await onSaveFinal(name, blob);
                    }}
                  />
                  <button className="btn btn-brouillon" onClick={onSaveRecord} disabled={saving}>
                    <i className="fas fa-save"></i> Brouillon
                  </button>
                </>
              )}
              {saving && <div className="muted">Enregistrement…</div>}
            </>
          )}
        </div>
      </div>

      {/* Tabs mobile */}
      <div className="um-tabs">
        <button className={`um-tab ${mobileTab === "form" ? "active" : ""}`} onClick={() => setMobileTab("form")}>
          Formulaire
        </button>
        <button className={`um-tab ${mobileTab === "preview" ? "active" : ""}`} onClick={() => setMobileTab("preview")}>
          Aperçu
        </button>
      </div>

      {/* Etats */}
      {(!mm || resolving) && <div className="alert um-alert">Chargement du modèle…</div>}

      {mm && !hasSource && !resolving && (
        <div className="alert um-alert">
          Modèle introuvable ou invalide.
          <button className="btn btn-outline" onClick={onBack} style={{ marginLeft: 8 }}>
            <i className="fas fa-arrow-left"></i> Retour
          </button>
        </div>
      )}

      {mm && hasSource && !templateAB && <div className="alert um-alert">Chargement du modèle…</div>}

      {/* Workspace */}
      {templateAB && (
        <div className="um-body">
          {/* LEFT: Form */}
          <div className={`um-pane ${mobileTab !== "form" ? "um-hide-mobile" : ""}`}>
            <div className="um-card">
              <div className="um-card-head">
                <div className="um-card-title">
                  <h2>Remplir les informations</h2>
                  <button className="btn btn-outline" onClick={() => setShowPlaceholders((s) => !s)}>
                    <i className="fas fa-eye"></i>{" "}
                    {showPlaceholders ? "Masquer placeholders" : "Afficher placeholders"}
                  </button>
                </div>

                <div className="um-toolrow">
                  <button className="btn btn-outline" onClick={() => setShowRelationFilter((s) => !s)}>
                    <i className="fas fa-user-plus"></i>{" "}
                    {showRelationFilter ? "Masquer relations" : "Ajouter relation"}
                  </button>

                  <button className="btn btn-outline" onClick={() => setShowMultiRelationFilter((s) => !s)}>
                    <i className="fas fa-users"></i>{" "}
                    {showMultiRelationFilter ? "Masquer multi-relations" : "Ajouter multi-relations"}
                  </button>
                </div>
              </div>

              <div className="um-card-body">
                {showRelationFilter && (
                  <RelationFilter
                    user={user}
                    onAddRelation={(relation) => {
                      setValues((prev) => {
                        const updated = { ...prev };
                        Object.keys(relation).forEach((key) => {
                          if (prev.hasOwnProperty(key)) updated[key] = relation[key];
                        });
                        return updated;
                      });
                    }}
                  />
                )}

                {showMultiRelationFilter && (
                  <MultiRelationFilter
                    user={user}
                    selectedModele={mm}
                    onGenerateBatch={(relations) => {
                      setMultiRelations(relations);
                      setMultiIndex(0);
                    }}
                  />
                )}

                <DynamicForm
                  fields={fields}
                  sections={sections}
                  values={values}
                  setValues={setValues}
                  showPlaceholders={showPlaceholders}
                  onFieldFocus={onFieldFocus}
                  onFieldChangeKey={onFieldChangeKey}
                  showSectionNav
                />
              </div>
            </div>
          </div>

          {/* RIGHT: Preview */}
          <div className={`um-pane ${mobileTab !== "preview" ? "um-hide-mobile" : ""}`}>
            <div className="um-card">
              <div className="um-preview-top">
                <strong>Aperçu du document</strong>
                <div className="muted">
                  {docHtmlLoading ? "Chargement…" : docHtmlErr ? "⚠️" : "✓"}
                </div>
              </div>

              <div className="um-preview-scroll" ref={previewScrollRef}>
                <div className="um-paper">
                  {docHtmlErr ? (
                    <div className="alert">{docHtmlErr}</div>
                  ) : renderedPreviewHtml ? (
                    <div dangerouslySetInnerHTML={{ __html: renderedPreviewHtml }} />
                  ) : (
                    <div className="muted">Aperçu en préparation…</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showShareModal && (
        <div
          className="modal-overlay show um-share-modal"
          style={{ display: "flex" }}
          onClick={() => setShowShareModal(false)}
        >
          <div
            className="share-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-custom">
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div className="share-icon-container">
                  <i className="fas fa-share-alt share-icon" />
                </div>
                <div>
                  <h5 className="modal-title-custom">Partager ce formulaire</h5>
                  <p className="modal-subtitle-custom">
                    Crée un lien public sécurisé pour collecter des réponses.
                  </p>
                </div>
              </div>

              <button
                className="btn-close-custom"
                onClick={() => setShowShareModal(false)}
                aria-label="Close"
                type="button"
              >
                ✕
              </button>
            </div>

            <div className="modal-body-custom">
              <div className="stats-row">
                <div className="stat-card">
                  <div className="stat-icon access">
                    <i className="fas fa-link" />
                  </div>
                  <div>
                    <div className="stat-title">Lien</div>
                    <p className="stat-value">
                      {generatedLink?.token ? "Généré" : "Non généré"}
                    </p>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon security">
                    <i className="fas fa-shield-alt" />
                  </div>
                  <div>
                    <div className="stat-title">Sécurité</div>
                    <p className="stat-value">
                      Expiration {shareConfig.expiresInDays || "—"} j
                    </p>
                  </div>
                </div>
              </div>

              <div className="tabs-container">
                <button
                  className={`tab-button ${shareTab === "links" ? "active" : ""}`}
                  onClick={() => setShareTab("links")}
                  type="button"
                >
                  <i className="fas fa-list" /> Liens actifs
                </button>
                <button
                  className={`tab-button ${shareTab === "create" ? "active" : ""}`}
                  onClick={() => setShareTab("create")}
                  type="button"
                >
                  <i className="fas fa-plus" /> Créer un lien
                </button>
                <button
                  className={`tab-button ${shareTab === "responses" ? "active" : ""}`}
                  onClick={() => setShareTab("responses")}
                  type="button"
                >
                  <i className="fas fa-inbox" /> Réponses
                </button>
              </div>

              {/* TAB: LINKS */}
              <div className={`tab-content ${shareTab === "links" ? "active" : ""}`}>
                {/* Liens actifs */}
                <div className="active-links-section">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h4 className="m-0">Liens actifs</h4>
                    <button className="btn btn-sm btn-link" onClick={fetchExistingLinks} title="Rafraîchir">
                      <i className="fas fa-sync-alt"></i>
                    </button>
                  </div>

                  {linksLoading ? (
                    <p style={{ margin: 0, opacity: 0.8 }}>Chargement…</p>
                  ) : activeLinks.length === 0 ? (
                    <p style={{ margin: 0, opacity: 0.8 }}>Aucun lien actif.</p>
                  ) : (
                    <div className="table-responsive active-links-table" style={{ maxHeight: 220 }}>
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Lien</th>
                            <th>Expire</th>
                            <th style={{ width: 200 }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeLinks.map((l) => (
                            <tr key={l.token}>
                              <td style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                <a href={`${window.location.origin}/s/${l.token}`} target="_blank" rel="noreferrer">
                                  {l.token}
                                </a>
                              </td>
                              <td>{l.expiresAt ? new Date(toMillisSafe(l.expiresAt)).toLocaleString() : "—"}</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => navigator.clipboard.writeText(`${window.location.origin}/s/${l.token}`)}
                                >
                                  Copier
                                </button>{" "}
                                <button className="btn btn-sm btn-outline-warning" onClick={() => handleDisableLink(l.token)}>
                                  Désactiver
                                </button>{" "}
                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteLink(l.token)}>
                                  Supprimer
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {archivedLinks.length > 0 && (
                    <details style={{ marginTop: 10 }}>
                      <summary style={{ cursor: "pointer" }}>
                        Liens désactivés / expirés ({archivedLinks.length})
                      </summary>

                      <div className="table-responsive active-links-table" style={{ maxHeight: 200, marginTop: 8 }}>
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Lien</th>
                              <th>Statut</th>
                              <th style={{ width: 220 }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {archivedLinks.map((l) => {
                              const expired = isLinkExpired(l);
                              const status = expired ? "expiré" : (l.status || "disabled");
                              return (
                                <tr key={l.token}>
                                  <td style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {l.token}
                                  </td>
                                  <td>{status}</td>
                                  <td>
                                    <button
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => navigator.clipboard.writeText(`${window.location.origin}/s/${l.token}`)}
                                    >
                                      Copier
                                    </button>{" "}
                                    {!expired && (l.status === "disabled") && (
                                      <>
                                        <button className="btn btn-sm btn-outline-success" onClick={() => handleEnableLink(l.token)}>
                                          Réactiver
                                        </button>{" "}
                                      </>
                                    )}
                                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteLink(l.token)}>
                                      Supprimer
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  )}
                </div>
              </div>

              {/* TAB: CREATE */}
              <div className={`tab-content ${shareTab === "create" ? "active" : ""}`}>
                <div style={{ display: "grid", gap: 12 }}>
                  <div>
                    <div className="form-label-custom">Expiration (jours)</div>
                    <select
                      className="form-select-custom"
                      value={shareConfig.expiresInDays}
                      onChange={(e) =>
                        setShareConfig((p) => ({ ...p, expiresInDays: Number(e.target.value) }))
                      }
                    >
                      <option value={0}>Jamais</option>
                      <option value={1}>1 jour</option>
                      <option value={7}>7 jours</option>
                      <option value={30}>30 jours</option>
                      <option value={90}>90 jours</option>
                    </select>
                  </div>

                  <div>
                    <div className="form-label-custom">Nombre max de réponses (optionnel)</div>
                    <input
                      className="form-control-custom"
                      type="number"
                      min="1"
                      placeholder="ex: 50"
                      value={shareConfig.maxSubmissions}
                      onChange={(e) =>
                        setShareConfig((p) => ({ ...p, maxSubmissions: e.target.value }))
                      }
                    />
                  </div>

                  <button
                    className="btn-primary-custom"
                    type="button"
                    onClick={handleCreateShareLink}
                    disabled={sharingLoading}
                  >
                    {sharingLoading ? "Création..." : "Générer le lien"}
                  </button>

                  {generatedLink?.token && (
                    <div style={{ marginTop: 8 }}>
                      <div className="form-label-custom">Lien généré</div>
                      <input
                        className="form-control-custom"
                        readOnly
                        value={generatedLink.url || buildShareUrl(generatedLink.token)}
                      />
                      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                        <button
                          className="btn-copy"
                          type="button"
                          onClick={async () => {
                            const url =
                              generatedLink.url || buildShareUrl(generatedLink.token);
                            await navigator.clipboard.writeText(url);
                          }}
                        >
                          Copier
                        </button>
                        {generatedLink.status === "disabled" ? (
                          <button
                            className="btn-enable"
                            type="button"
                            onClick={() => handleEnableLink(generatedLink.token)}
                            style={{ background: "rgba(46, 204, 113, 0.16)", color: "#1e9a52", border: "none", borderRadius: "10px", padding: "8px 10px", fontWeight: "700", cursor: "pointer", fontSize: "12px" }}
                          >
                            Réactiver
                          </button>
                        ) : (
                          <button
                            className="btn-disable"
                            type="button"
                            onClick={() => handleDisableLink(generatedLink.token)}
                          >
                            Désactiver
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* TAB: RESPONSES */}
              <div className={`tab-content ${shareTab === "responses" ? "active" : ""}`}>
                <div style={{ fontWeight: 800, marginBottom: 10 }}>Réponses récentes</div>
                {recentSubmissions.length === 0 ? (
                  <div>Aucune réponse pour ce modèle.</div>
                ) : (
                  <div style={{ display: "grid", gap: 10 }}>
                    {recentSubmissions.map((s) => (
                      <div key={s.id} className="link-card">
                        <div>
                          <div style={{ fontWeight: 800, fontSize: 13 }}>
                            {s.createdAt?.toDate ? s.createdAt.toDate().toLocaleString() : "—"}
                          </div>
                          <div style={{ fontSize: 12, color: "#6c757d" }}>
                            {s.submissionId || s.id}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* JSON Viewer Modal */}
      {viewingSubmission && (
        <div className="modal-overlay show" onClick={() => setViewingSubmission(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Détail réponse</h3>
              <button className="close-btn" onClick={() => setViewingSubmission(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <pre style={{background: "#f8f9fa", padding: 10, borderRadius: 5, maxHeight: 400, overflow: "auto"}}>
                {JSON.stringify(viewingSubmission.payload?.values || viewingSubmission.payload || {}, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
